import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getShopByOwner, getShop, updateShop } from '../../services/shopService';
import Toast from '../../components/Toast';

const Shipping = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    cost: '',
    freeThreshold: ''
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  useEffect(() => {
    const loadShop = async () => {
      try {
        const shops = await getShopByOwner(currentUser.uid);
        if (shops.length > 0) {
          setShop(shops[0]);
          const shipping = shops[0].shipping || {};
          setFormData({
            cost: shipping.cost?.toString() || '5',
            freeThreshold: shipping.freeThreshold?.toString() || ''
          });
        }
      } catch (err) {
        console.error('Errore nel caricamento del negozio:', err);
      } finally {
        setLoading(false);
      }
    };
    loadShop();
  }, [currentUser]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!shop) return;

    try {
      setSaving(true);
      const cost = parseFloat(formData.cost) || 0;
      const freeThreshold = formData.freeThreshold ? parseFloat(formData.freeThreshold) : null;

      await updateShop(shop.id, {
        shipping: {
          cost,
          freeThreshold
        }
      });

      // Aggiorna shop locale
      setShop(prev => ({
        ...prev,
        shipping: { cost, freeThreshold }
      }));

      setToastMessage('Costi di spedizione salvati con successo');
      setToastVariant('success');
      setShowToast(true);
    } catch (err) {
      console.error('Errore nel salvataggio:', err);
      setToastMessage('Errore nel salvataggio dei costi di spedizione');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      <h5 className="fw-bold mb-4">
        <i className="bi bi-truck me-2"></i>
        Costi di Spedizione
      </h5>

      <Card className="rounded-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Costo spedizione base (€)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formData.cost}
                onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                placeholder="Es. 5.00"
                required
              />
              <Form.Text className="text-muted">
                Il costo base di spedizione che verrà applicato a ogni ordine.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Soglia spedizione gratuita (€) — opzionale</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formData.freeThreshold}
                onChange={(e) => setFormData(prev => ({ ...prev, freeThreshold: e.target.value }))}
                placeholder="Es. 50.00"
              />
              <Form.Text className="text-muted">
                Se impostato, gli ordini con subtotale superiore a questa soglia avranno spedizione gratuita.
                Lascia vuoto per disabilitare.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="dark"
                type="submit"
                disabled={saving}
                className="rounded-pill px-4"
                style={{ backgroundColor: '#0A0A17' }}
              >
                {saving ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i> Salva
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Anteprima */}
      {shop?.shipping && (
        <Card className="rounded-4 border-0 shadow-sm mt-4">
          <Card.Body className="p-4">
            <h6 className="fw-bold mb-3">Anteprima per il cliente</h6>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Costo spedizione</span>
              <span>
                {shop.shipping.cost === 0 ? 'Gratuita' : `€${shop.shipping.cost.toFixed(2)}`}
              </span>
            </div>
            {shop.shipping.freeThreshold && (
              <div className="d-flex justify-content-between">
                <span className="text-muted">Spedizione gratuita sopra</span>
                <span className="text-success fw-medium">€{shop.shipping.freeThreshold.toFixed(2)}</span>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default Shipping;
